import React, { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { AttachedFileProps } from '../../Contact/utils/upload.types';
import Attachment from './Attachment';
import { ReactComponent as AttachFileSvg } from './icons/attach_file.svg';
import { FileInfo, HiddenFileInput, UploadButton, Wrapper } from './styled';

export type FileUploadProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onDrop' | 'accept'
> & {
  name: string;
  fluid?: boolean;
  uploadedFiles: AttachedFileProps[];
  accept: string[];
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDrop: (fileList: FileList) => void;
  onRemove: (id: string) => void;
  classNameFilesWrapper?: string;
};

const FileUpload = ({
  name,
  onChange,
  onDrop,
  onRemove,
  accept,
  uploadedFiles,
  fluid = false,
  className,
  classNameFilesWrapper,
  ...props
}: FileUploadProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDrag, setIsDrag] = useState(false);

  const onFileRemove = (id: string) => {
    onRemove?.(id);

    const index = uploadedFiles.findIndex((file) => file.id === id);

    const dt = new DataTransfer();

    if (inputRef.current && inputRef.current.files) {
      const files = Array.from(inputRef.current.files);

      files.forEach((file, fileIndex) => {
        if (index !== fileIndex) dt.items.add(file);
      });

      inputRef.current.files = dt.files;
    }
  };

  return (
    <>
      <label
        htmlFor={name}
        onDrop={(event) => {
          event.preventDefault();
          if (onDrop && event.dataTransfer.files.length > 0) {
            onDrop(event.dataTransfer.files);
          }
          setIsDrag(false);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDrag(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDrag(false);
        }}
        className={`ghc-file-upload-button ${className ?? ''}`}
      >
        <UploadButton fluid={fluid} isDrag={isDrag}>
          <AttachFileSvg />
          <span>{t('attachFiles')}</span>
        </UploadButton>
        <FileInfo>{t('fileInfo')}</FileInfo>
      </label>
      <HiddenFileInput
        {...props}
        innerRef={inputRef}
        accept={accept.join(', ')}
        type="file"
        multiple
        name={name}
        id={name}
        onChange={(el) => {
          onChange?.(el);
        }}
      />
      <Wrapper
        className={`ghc-uploaded-files-wrapper ${classNameFilesWrapper ?? ''}`}
      >
        {uploadedFiles.map((attachment) => (
          <Attachment
            fileName={attachment.file.name}
            status={attachment.status}
            percentage={attachment.percentage ?? 0}
            onRemove={onFileRemove}
            id={attachment.id}
            key={attachment.id}
          />
        ))}
      </Wrapper>
    </>
  );
};

export default FileUpload;
